import React from "react";
import { useParams, Link } from "react-router-dom";
import useSWR from "swr";
import axios from "axios";
import { formatIDR, formatDate, terbilang } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Printer, ChevronLeft, Building2, ShieldOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fetcher = (url) => axios.get(url).then((r) => r.data);

export default function TenantPortalReceipt() {
  const { token, paymentId } = useParams();
  const { data, error } = useSWR(
    token && paymentId ? `${BACKEND_URL}/api/portal/${token}/receipt/${paymentId}` : null,
    fetcher
  );

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div className="text-center space-y-2">
          <ShieldOff className="h-10 w-10 mx-auto text-destructive" />
          <div className="font-medium">Kwitansi tidak ditemukan.</div>
          <Link to={`/portal/${token}`} className="text-primary text-sm hover:underline">← Kembali ke portal</Link>
        </div>
      </div>
    );
  }
  if (!data) return <div className="p-6 text-sm text-muted-foreground">Memuat...</div>;

  const { payment, bill, unit, tenant } = data;

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-10">
        <div className="no-print mb-4 flex items-center justify-between">
          <Link to={`/portal/${token}`} className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground">
            <ChevronLeft className="h-4 w-4" /> Kembali
          </Link>
          <Button className="gap-2" onClick={() => window.print()} data-testid="portal-print-receipt-button">
            <Printer className="h-4 w-4" /> Cetak / Simpan PDF
          </Button>
        </div>

        <div className="print-container bg-card border border-border/60 shadow-sm rounded-lg p-8 sm:p-12" data-testid="portal-receipt">
          <header className="flex items-start justify-between gap-6 pb-6 border-b border-border/60">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-md bg-primary text-primary-foreground grid place-items-center">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <div className="font-display text-lg font-semibold tracking-tight">Aplikasi Kontrakan Surbakti</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Kwitansi Pembayaran</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">No. Kwitansi</div>
              <div className="font-mono text-sm mt-1">{payment.receipt_number}</div>
              <div className="text-xs text-muted-foreground mt-1">{formatDate(payment.paid_at)}</div>
            </div>
          </header>

          <section className="grid grid-cols-2 gap-6 py-6 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Diterima dari</div>
              <div className="font-medium">{tenant?.name}</div>
              {tenant?.phone && <div className="text-xs text-muted-foreground">{tenant.phone}</div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Unit Kontrakan</div>
              <div className="font-medium">{unit?.code} — {unit?.name}</div>
              {unit?.address && <div className="text-xs text-muted-foreground">{unit.address}</div>}
            </div>
          </section>

          <section className="py-4 border-y border-border/60">
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Uang sejumlah</div>
            <div className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
              {formatIDR(payment.amount)}
            </div>
            <div className="italic text-sm text-muted-foreground mt-2 first-letter:uppercase">
              {terbilang(payment.amount)}
            </div>
          </section>

          <section className="py-6 space-y-2 text-sm">
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Untuk pembayaran</div>
            {bill && <Row label="Periode Sewa" value={`${formatDate(bill.period_start)} – ${formatDate(bill.period_end)}`} />}
            {bill && <Row label="Jatuh Tempo" value={formatDate(bill.due_date)} />}
            {bill && <Row label="Nilai Tagihan" value={formatIDR(bill.amount)} />}
            {bill?.late_fee > 0 && <Row label="Denda Keterlambatan" value={formatIDR(bill.late_fee)} />}
            <Row label="Metode Pembayaran" value={payment.method === "cash" ? "Cash" : "Transfer"} />
            {payment.note && <Row label="Catatan" value={payment.note} />}
          </section>

          <footer className="mt-8 pt-4 border-t border-border/60 text-center text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Dokumen ini sah tanpa cap basah · Aplikasi Kontrakan Surbakti
          </footer>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value || "—"}</span>
    </div>
  );
}
